// The effects layer: a canvas over the map, the frame loop that drives the director, the camera's slow drift during
// a replay's chapters, chapter sounds, and the sound settings.

import type maplibregl from "maplibre-gl";
import { useEffect, useRef, type RefObject } from "react";
import type { GedYear, NamedEvents } from "../lib/data";
import { chapterAt, familyMask } from "../lib/replay";
import { useApp } from "../lib/store";
import { audio } from "./audio";
import { Director, type Frame } from "./director";
import { eraOf, indexGed, indexNamed, shakeAt, unitPx, type FxEvents } from "./logic";
import { ADDITIVE, Anchors, LAYERS, Particles, type View } from "./particles";
import { SpriteRenderer } from "./renderer";
import { motionFx, useFx } from "./state";

const DEG = Math.PI / 180;
const namedIdx = new WeakMap<NamedEvents, FxEvents>();
const gedIdx = new WeakMap<GedYear, FxEvents>();

function sourcesOf(named: NamedEvents[], ged: GedYear[]): FxEvents[] {
  const out: FxEvents[] = [];
  for (const ch of named) {
    let ix = namedIdx.get(ch);
    if (!ix) namedIdx.set(ch, (ix = indexNamed(ch)));
    out.push(ix);
  }
  for (const y of ged) {
    let ix = gedIdx.get(y);
    if (!ix) gedIdx.set(y, (ix = indexGed(y)));
    out.push(ix);
  }
  return out;
}

/** Settings → audio engine; the first click or key press unlocks sound. */
function useAudioSettings() {
  const sound = useFx((s) => s.sound);
  const volume = useFx((s) => s.volume);
  const level = useFx((s) => s.level);
  useEffect(() => {
    audio.onBlocked = (b) => useFx.getState().setAudioBlocked(b);
    audio.setEnabled(sound && level !== "off", volume);
    if (!sound || level === "off") {
      useFx.getState().setAudioBlocked(false);
      return;
    }
    // events that count as a user's activation: a mouse press, a key, or (on touch screens) the finger lifting
    const unlock = () => audio.unlock();
    const events = ["pointerdown", "pointerup", "touchend", "keydown", "click"] as const;
    for (const ev of events) window.addEventListener(ev, unlock, true);
    return () => { for (const ev of events) window.removeEventListener(ev, unlock, true); };
  }, [sound, volume, level]);
}

if (import.meta.env.DEV) (window as unknown as { __audio: typeof audio }).__audio = audio;

export function FxLayer({ mapRef, stageRef, named, ged }: {
  mapRef: RefObject<maplibregl.Map | null>; stageRef: RefObject<HTMLDivElement | null>; named: NamedEvents[]; ged: GedYear[];
}) {
  const canvas = useRef<HTMLCanvasElement>(null);
  const flashEl = useRef<HTMLDivElement>(null);
  const director = useRef<Director | null>(null);
  const sources = useRef<FxEvents[]>([]);
  const level = useFx((s) => s.level);
  useAudioSettings();

  useEffect(() => {
    sources.current = sourcesOf(named, ged);
    director.current?.setSources(sources.current);
  }, [named, ged]);

  useEffect(() => {
    const cv = canvas.current, stage = stageRef.current;
    if (level === "off" || !cv || !stage) return;
    let gl: SpriteRenderer;
    try {
      gl = new SpriteRenderer(cv);
    } catch (e) {
      console.warn("Effects are off: no WebGL2 here.", e);
      return;
    }
    const coarse = window.matchMedia("(pointer: coarse)").matches || window.innerWidth < 900;
    const forced = import.meta.env.DEV ? Number(new URLSearchParams(window.location.search).get("fxq")) : 0; // dev: judge full quality on software GL
    const quality = forced || (gl.software ? 0.35 : coarse ? 0.6 : 1) * (level === "simple" ? 0.65 : 1);
    const scale = gl.software ? 0.6 : Math.min(window.devicePixelRatio || 1, 1.5);
    const P = new Particles(quality >= 0.9 ? 5200 : 2400), A = new Anchors(), D = new Director(P, A);
    D.setSources(sources.current);
    director.current = D;

    // `sim` is the effects' own clock: the sum of clamped frame steps (a hidden tab pauses it, a slow one slows it),
    // so particles, places and delayed blasts all age together
    let raf = 0, last = performance.now(), sim = 0, tPrev = useApp.getState().t, wasDrawn = true;
    let lastKey = "", lastCh = -2, driftDir = 1, userAt = -1e9, lastAmbience = 0, lostSeen = false, frame: Frame | null = null;
    const atrocity = new Map<number, boolean>(); // chapter → about an atrocity: a moment of quiet while the replay waits
    let askedAt = -1, askedCh = -2;
    const wind = { x: 5.5, y: 1.6 }; // a westerly
    const map = () => mapRef.current;
    const onUser = () => { userAt = performance.now(); };
    let watched: maplibregl.Map | null = null; // the map is created after this effect runs (parents' effects run last)
    const onHide = () => audio.setHidden(document.hidden);
    onHide();
    document.addEventListener("visibilitychange", onHide);

    const loop = (time: number) => {
      raf = requestAnimationFrame(loop);
      const mp = map();
      if (!mp) return;
      if (gl.lost) {
        // until the browser gives the context back: no shaking, no white-out, no sound bed
        if (!lostSeen) {
          lostSeen = true;
          stage.style.transform = "";
          if (flashEl.current) flashEl.current.style.opacity = "0";
          audio.hush();
        }
        return;
      }
      lostSeen = false;
      if (watched !== mp) {
        for (const ev of ["dragstart", "wheel", "touchstart"] as const) mp.on(ev, onUser);
        watched = mp;
      }
      const dt = Math.min(0.1, Math.max(0, (time - last) / 1000));
      last = time;
      const st = useApp.getState(), fx = useFx.getState();
      const motion = motionFx(fx);
      const w = stage.clientWidth, h = stage.clientHeight;
      const zoom = mp.getZoom(), pitch = mp.getPitch() * DEG, bearing = mp.getBearing() * DEG;
      const view: View = {
        w, h, unit: unitPx(zoom), cosB: Math.cos(bearing), sinB: Math.sin(bearing), cosP: Math.cos(pitch),
        zf: 0.45 + 0.55 * Math.sin(pitch),
      };
      const project = (lon: number, lat: number): [number, number] => {
        const p = mp.project([lon, lat]);
        return [p.x, p.y];
      };
      sim += dt;
      const now = sim;
      const center = mp.getCenter(), centerLon = center.lng;
      A.project(project, view, centerLon, center.lat, zoom, now);
      frame = {
        now, dt, t: st.t, tPrev, span: st.span, playing: st.playing, conflicts: st.conflicts!, focus: st.focus, kinds: st.kinds,
        events: st.layers.events && !!st.conflicts, view,
        replay: st.replay ? { t0: st.replay.t0, t1: st.replay.t1, holding: st.replay.hold > 0 && st.playing } : null,
        project, centerLon, motion, quality, sound: fx.sound,
      };
      if (st.conflicts) D.frame(frame);
      tPrev = st.t;

      // replay: chapter sounds, the chapter's battle when the camera gets there, the camera's drift while it waits
      const r = st.replay;
      if (r) {
        const k = chapterAt(r.chapters, st.t);
        if (r.key !== lastKey) atrocity.clear();
        const here = r.chapters[k];
        if (here?.r && !atrocity.has(k) && (k !== askedCh || now - askedAt > 0.5)) { // at once, then again while loading
          askedAt = now;
          askedCh = k;
          const a = st.conflicts?.byId.has(here.r) ? false : D.isMemorial(here.r); // a campaign is not an atrocity
          if (a !== undefined) atrocity.set(k, a);
        }
        if (r.key !== lastKey) {
          lastKey = r.key;
          lastCh = k;
          fx.setStats({ battles: 0, burning: 0 });
          if (fx.sound) audio.stinger(eraOf(r.t0));
        } else if (k !== lastCh) {
          lastCh = k;
          const ch = r.chapters[k];
          if (ch && Math.abs(st.t - ch.t) < 1e-6) {
            if (fx.sound) {
              if (!ch.r || atrocity.get(k) === false) audio.stinger(eraOf(ch.t)); // none for a massacre: the bell, and quiet
              if (ch.v) audio.whoosh(1.7);
            }
            // the chapter's battle, or (for a campaign) its battles, when the camera arrives
            const ci = ch.r && st.conflicts ? st.conflicts.byId.get(ch.r) : undefined;
            if (ch.r) D.hero(ch.r, ci != null ? familyMask(st.conflicts!, ci) : null, ch.t, ch.v ? 1.45 : 0.2, () => frame);
            driftDir = -driftDir;
          }
        }
        if (motion && r.hold > 0.4 && st.playing && !mp.isMoving() && time - userAt > 3500) {
          const b = mp.getBearing();
          const nb = Math.abs(b + driftDir * 1.3 * dt) < 16 ? b + driftDir * 1.3 * dt : b;
          mp.jumpTo({ bearing: nb, zoom: Math.min(mp.getMaxZoom(), zoom + 0.016 * dt) }, { fxDrift: true });
        }
        audio.setQuiet(r.hold > 0 && atrocity.get(k) === true);
      } else {
        lastKey = "";
        audio.setQuiet(false);
      }

      // ambience: the fighting on screen, while history is moving (a few updates a second are plenty); around the
      // places burning, the period's battle goes on (louder while a replay waits at a chapter)
      if (fx.sound && now - lastAmbience > 0.25) {
        lastAmbience = now;
        audio.ambience(st.playing ? D.level : 0, !!r, st.t, st.playing ? D.fire : 0);
        if (st.playing) audio.battlefield(D.level, st.t, !!r && r.hold > 0, () => D.place(view));
      }

      P.update(dt, wind.x, wind.y);
      // shake and flash
      const [sx, sy, rot] = motion && D.trauma > 0 ? shakeAt(D.trauma, now) : [0, 0, 0];
      stage.style.transform = sx || sy ? `translate3d(${sx.toFixed(2)}px, ${sy.toFixed(2)}px, 0) rotate(${rot.toFixed(3)}deg)` : "";
      if (flashEl.current) flashEl.current.style.opacity = motion && D.flash > 0 ? D.flash.toFixed(3) : "0";
      if (!P.n) {
        if (wasDrawn) { gl.resize(w, h, scale); gl.begin(); wasDrawn = false; }
        return;
      }
      wasDrawn = true;
      P.build(A, view, now);
      gl.resize(w, h, scale);
      gl.begin();
      for (let L = 0; L < LAYERS; L++) gl.draw(P.out[L], P.counts[L], ADDITIVE[L]);
    };
    raf = requestAnimationFrame(loop);
    return () => {
      cancelAnimationFrame(raf);
      for (const ev of ["dragstart", "wheel", "touchstart"] as const) watched?.off(ev, onUser);
      document.removeEventListener("visibilitychange", onHide);
      audio.hush();
      stage.style.transform = "";
      director.current = null;
      gl.destroy(useFx.getState().level === "off"); // turned off: the canvas goes, so its context goes too
    };
  }, [level, mapRef, stageRef]);

  if (level === "off") return null;
  return (
    <>
      <canvas ref={canvas} className="fx-canvas" aria-hidden data-testid="fx-canvas" />
      <div ref={flashEl} className="fx-flash" aria-hidden />
    </>
  );
}
